import "@/App.css";
import { useEffect } from "react";
import { installDownloadInterceptor } from "@/lib/downloadPaths";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import IncomeTax from "@/pages/IncomeTax";
import IncomeTaxLogin from "@/pages/IncomeTaxLogin";
import IncomeTaxLayout from "@/components/IncomeTaxLayout";
import ITClients from "@/pages/ITClients";
import ITRFiling from "@/pages/ITRFiling";
import ITTDSReturns from "@/pages/ITTDSReturns";
import ITDocuments from "@/pages/ITDocuments";
import SuperAdmin from "@/pages/SuperAdmin";
import Customers from "@/pages/Customers";
import Products from "@/pages/Products";
import Invoices from "@/pages/Invoices";
import CreateInvoice from "@/pages/CreateInvoice";
import EditInvoice from "@/pages/EditInvoice";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Settings from "@/pages/Settings";
import SoftwareCreation from "@/pages/SoftwareCreation";
import Reports from "@/pages/Reports";
import GSTR1 from "@/pages/GSTR1";
import RecurringInvoices from "@/pages/RecurringInvoices";
import UserManagement from "@/pages/UserManagement";
import Challans from "@/pages/Challans";
import Purchases from "@/pages/Purchases";
import Godown from "@/pages/Godown";
import Manufacturing from "@/pages/Manufacturing";
import StockLedger from "@/pages/StockLedger";
import Ledger from "@/pages/Ledger";
import InstallApp from "@/pages/InstallApp";
import BankStatements from "@/pages/BankStatements";
import LedgerViews from "@/pages/LedgerViews";
import ScheduleIII from "@/pages/ScheduleIII";
import Vouchers from "@/pages/Vouchers";
import Notes from "@/pages/Notes";
import NoteEditor from "@/pages/NoteEditor";
import PurchaseNoteEditor from "@/pages/PurchaseNoteEditor";
import Letterheads from "@/pages/Letterheads";
import TransportInvoices from "@/pages/TransportInvoices";
import CreateTransportInvoice from "@/pages/CreateTransportInvoice";
import TransportInvoiceDetail from "@/pages/TransportInvoiceDetail";
import RecycleBin from "@/pages/RecycleBin";
import PurchaseBillExtractor from "@/pages/PurchaseBillExtractor";
import SalesBillExtractor from "@/pages/SalesBillExtractor";
import GSTR2AReconciliation from "@/pages/GSTR2AReconciliation";
import PdfToolsIndex from "@/pages/PdfToolsIndex";
import Wallet from "@/pages/Wallet";
import { useGlobalAutoCap } from "@/lib/useGlobalAutoCap";
import { useGlobalEnterNav } from "@/lib/useGlobalEnterNav";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-50"><div className="animate-pulse text-slate-400 text-lg">Loading...</div></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user && user.gst_enabled === false && user.role !== "super_admin") return <Navigate to="/income-tax" replace />;
  return children;
}

function ITProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen" style={{ background: "#052e2b" }}><div className="animate-pulse text-emerald-200 text-lg">Loading...</div></div>;
  if (user === false) return <Navigate to="/income-tax/login" replace />;
  if (!user?.income_tax_enabled && user?.role !== "super_admin") return <Navigate to="/income-tax/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  // Global title-case auto-capitalisation — active on every text input +
  // textarea in the whole app (including raw HTML tags in TI forms). Users
  // can bypass with Shift (per-keystroke) or Caps Lock (persistent).
  useGlobalAutoCap();
  useGlobalEnterNav();
  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-50"><div className="animate-pulse text-slate-400 text-lg">Loading...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === "super_admin" ? "/super-admin" : "/dashboard"} replace /> : <LoginPage />} />

      {/* ===== Income Tax Module — fully independent (own login, layout, dashboard) ===== */}
      <Route path="/income-tax/login" element={user && (user.income_tax_enabled || user.role === "super_admin") ? <Navigate to="/income-tax" replace /> : <IncomeTaxLogin />} />
      <Route path="/income-tax" element={<ITProtectedRoute><IncomeTaxLayout /></ITProtectedRoute>}>
        <Route index element={<IncomeTax />} />
        <Route path="clients" element={<ITClients />} />
        <Route path="itr" element={<ITRFiling />} />
        <Route path="tds" element={<ITTDSReturns />} />
        <Route path="documents" element={<ITDocuments />} />
      </Route>

      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="super-admin" element={user?.role === "super_admin" ? <SuperAdmin /> : <Navigate to="/dashboard" replace />} />
        <Route path="wallet" element={user?.role === "admin" || user?.role === "super_admin" ? <Wallet /> : <Navigate to="/dashboard" replace />} />
        <Route path="customers" element={<Customers />} />
        <Route path="products" element={<Products />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/create" element={<CreateInvoice />} />
        <Route path="invoices/:id/edit" element={<EditInvoice />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="settings" element={<Settings />} />
        <Route path="software-creation" element={<SoftwareCreation />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/gstr1" element={<GSTR1 />} />
        <Route path="recurring" element={<RecurringInvoices />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="godown" element={<Godown />} />
            <Route path="manufacturing" element={<Manufacturing />} />
            <Route path="stock" element={<StockLedger />} />
            <Route path="ledger" element={<Ledger />} />
            <Route path="install" element={<InstallApp />} />
            <Route path="challans" element={<Challans />} />
            <Route path="ledger-views" element={<LedgerViews />} />
            <Route path="schedule-iii" element={<ScheduleIII />} />
            <Route path="vouchers" element={<Vouchers />} />
            <Route path="bank" element={<BankStatements />} />
            <Route path="notes" element={<Notes />} />
            <Route path="notes/from-invoice/:invoiceId" element={<NoteEditor />} />
            <Route path="notes/:id/edit" element={<NoteEditor />} />
            <Route path="purchase-notes/:id/edit" element={<PurchaseNoteEditor />} />
            <Route path="letterheads" element={<Letterheads />} />
            <Route path="transport-invoices" element={<TransportInvoices />} />
            <Route path="transport-invoices/create" element={<CreateTransportInvoice />} />
            <Route path="transport-invoices/:id/edit" element={<CreateTransportInvoice />} />
            <Route path="transport-invoices/:id" element={<TransportInvoiceDetail />} />
            <Route path="recycle-bin" element={<RecycleBin />} />
            <Route path="pdf-tools" element={<PdfToolsIndex />} />
            <Route path="pdf-tools/purchase-bills" element={<PurchaseBillExtractor />} />
            <Route path="pdf-tools/sales-bills" element={<SalesBillExtractor />} />
            <Route path="pdf-tools/gstr2a" element={<GSTR2AReconciliation />} />
        <Route path="users" element={<UserManagement />} />
      </Route>
    </Routes>
  );
}

function App() {
  useEffect(() => { installDownloadInterceptor(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
